import { useEffect, useState } from 'react';

/**
 * Custom useMediaQuery hook for media query matching
 * SSR-safe and automatically updates when viewport changes
 * Initializes with the actual media query value on client-side to prevent false negatives
 */
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    // Lazy initialization: get actual value on first render (client-side only)
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    // Update state if it differs from initial value
    if (mediaQuery.matches !== matches) {
      setMatches(mediaQuery.matches);
    }

    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, [query, matches]);

  return matches;
};

// Screen size breakpoints matching Constellation design tokens
const BREAKPOINTS = {
  sm: 320,
  md: 480,
  lg: 768,
  xl: 1024,
  xxl: 1280,
} as const;

export type ScreenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type ScreenType = 'mobile' | 'tablet' | 'desktop';

export const useScreenSize = () => {
  // Use pixel-based media queries matching Constellation breakpoints
  const isScreenXS = useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`); // < 320px
  const isScreenSM = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`); // 320-479px
  const isScreenMD = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`); // 480-767px
  const isScreenLG = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px) and (max-width: ${BREAKPOINTS.xl - 1}px)`); // 768-1023px
  const isScreenXL = useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px) and (max-width: ${BREAKPOINTS.xxl - 1}px)`); // 1024-1279px
  const isScreenXXL = useMediaQuery(`(min-width: ${BREAKPOINTS.xxl}px)`); // 1280px+

  // Screen type groupings (matches legacy browserUtils)
  const isScreenMobile = isScreenXS || isScreenSM; // 0-479px
  const isScreenTablet = isScreenMD || isScreenLG; // 480-1023px
  const isScreenDesktop = isScreenXL || isScreenXXL; // 1024px+

  // Greater-than-or-equal-to helpers (matches legacy AppSelectors)
  const isScreenLgGTE = isScreenLG || isScreenXL || isScreenXXL; // >= 768px
  const isScreenXlGTE = isScreenXL || isScreenXXL; // >= 1024px
  const isScreenXxlGTE = isScreenXXL; // >= 1280px

  return {
    isScreenXS,
    isScreenSM,
    isScreenMD,
    isScreenLG,
    isScreenXL,
    isScreenXXL,
    isScreenMobile,
    isScreenTablet,
    isScreenDesktop,
    isScreenLgGTE,
    isScreenXlGTE,
    isScreenXxlGTE,
  };
};
